import { FileCheck } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { CofaPipelinePage } from './pages/CofaPipelinePage'

/**
 * CofA Docs — CofA management dashboard for admin + regulatory +
 * operations (not visible to viewer or other roles).
 *
 * Sprint 04b repurpose (2026-04-20): the module directory name
 * `cofa-pipeline` is a legacy misnomer. This module now renders the
 * full management surface at `/cofa-docs` — vendor-grouped lot
 * registry with inline admin actions (Rename/Replace/Remove/Upload),
 * primary batch-upload launcher, compact pipeline summary strip with
 * drill-in to the full kanban. The read-only library at `/cofa` is
 * rendered by the `cofa-documents` module (also a misnomer post-swap).
 *
 * Sprint 04 plan D-2 (vendor-grouped surface on /cofa-docs) was the
 * correct placement per foundation spec implementation-spec.md:699
 * BUT putting admin affordances on the all-users library page
 * conflated two personas. D-7 (Sprint 04b) corrects that by splitting
 * by persona: /cofa = reference (all users), /cofa-docs = management
 * (admin + regulatory + operations only).
 */
export default defineModule({
  id: 'cofa-pipeline',
  name: 'CofA Management',
  shortName: 'CofA Management',
  icon: FileCheck,
  navSection: 'regulatory',
  navOrder: 20,
  urlNamespace: '/cofa-docs',
  routes: [{ path: '/', element: CofaPipelinePage, label: 'CofA Management' }],
  // Admin-only mutation surface — viewer/sales/warehouse are excluded.
  // Operations is included because SPI ops staff manage vendor CofAs.
  permissions: ['admin', 'regulatory', 'operations', 'operations_logistics', 'purchasing'],
  densityDefault: 'comfortable',
  mobileStrategy: 'desktop-only',

  keyboardShortcut: 'g d',

  commandPaletteEntries: [
    {
      label: 'Go to CofA Management',
      keywords: ['cofa', 'management', 'admin', 'upload', 'approve', 'reject', 'lot', 'vendor', 'docs'],
      path: '/',
      group: 'modules',
      shortcut: 'g d',
    },
  ],

  deepLinkHandlers: [
    {
      entity: 'lot',
      label: 'Manage CofA for this lot',
      buildPath: (lotId) => `/?lot=${lotId}`,
    },
    {
      entity: 'product',
      label: 'Filter CofA Management by product',
      buildPath: (productId) => `/?product=${productId}`,
    },
    {
      entity: 'vendor',
      label: 'Filter CofA Management by vendor',
      buildPath: (vendorId) => `/?vendor=${vendorId}`,
    },
  ],
})
